import React, { type ReactNode, useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  ChevronRight,
  Download,
  Globe,
  HardDrive,
  Info,
  LayoutDashboard,
  Lock,
  Paintbrush,
  Settings2,
  Shield,
  Type,
} from 'lucide-react';

import { closeBudget, loadPrefs } from 'loot-core/client/actions';
import { isElectron } from 'loot-core/shared/environment';
import { listen } from 'loot-core/src/platform/client/fetch';

import { useGlobalPref } from '../../hooks/useGlobalPref';
import { useMetadataPref } from '../../hooks/useMetadataPref';
import { useDispatch } from '../../redux';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button2';
import { Input } from '../ui/input2';
import { Text } from '../common/Text';
import { FormField, FormLabel } from '../forms';
import { MOBILE_NAV_HEIGHT } from '../mobile/MobileNavTabs';
import { Page } from '../Page';
import { useResponsive } from '../responsive/ResponsiveProvider';
import { useServerVersion } from '../ServerContext';

import { AuthSettings } from './AuthSettings';
import { Backups } from './Backups';
import { BudgetTypeSettings } from './BudgetTypeSettings';
import { EncryptionSettings } from './Encryption';
import { ExperimentalFeatures } from './Experimental';
import { ExportBudget } from './Export';
import { FormatSettings } from './Format';
import { LanguageSettings } from './LanguageSettings';
import { RepairTransactions } from './RepairTransactions';
import { ResetCache, ResetSync } from './Reset';
import { ThemeSettings } from './Themes';
import { Setting } from './UI';

function About() {
  const version = useServerVersion();

  return (
    <Setting>
      <Text>
        <Trans>
          <strong>Actual</strong> is a super fast privacy-focused app for
          managing your finances.
        </Trans>
      </Text>
      <div
        style={{
          flexDirection: 'column',
          gap: 10,
        }}
        className="flex flex-col gap-2 small:grid small:grid-cols-2 small:grid-rows-2 small:gap-x-8 small:[grid-auto-flow:column]"
        data-vrt-mask
      >
        <Text>
          <Trans>
            Client version: {{ version: `v${window.Actual?.ACTUAL_VERSION}` }}
          </Trans>
        </Text>
        <Text>
          <Trans>Server version: {{ version }}</Trans>
        </Text>
      </div>
    </Setting>
  );
}

function IDName({ children }: { children: ReactNode }) {
  return <Text style={{ fontWeight: 500 }}>{children}</Text>;
}

function AdvancedAbout() {
  const [budgetId] = useMetadataPref('id');
  const [groupId] = useMetadataPref('groupId');

  return (
    <Setting>
      <Text>
        <Trans>
          <strong>IDs</strong> are the names Actual uses to identify your budget
          internally. There are several different IDs associated with your
          budget. The Budget ID is used to identify your budget file. The Sync
          ID is used to access the budget on the server.
        </Trans>
      </Text>
      <Text>
        <Trans>
          <IDName>Budget ID:</IDName> {{ budgetId }}
        </Trans>
      </Text>
      <Text className="text-foreground">
        <Trans>
          <IDName>Sync ID:</IDName> {{ syncId: groupId || '(none)' }}
        </Trans>
      </Text>
      {/* low priority todo: eliminate some or all of these, or decide when/if to show them */}
      {/* <Text>
        <IDName>Cloud File ID:</IDName> {prefs.cloudFileId || '(none)'}
      </Text>
      <Text>
        <IDName>User ID:</IDName> {prefs.userId || '(none)'}
      </Text> */}
    </Setting>
  );
}

export function Settings() {
  const { t } = useTranslation();
  const [floatingSidebar] = useGlobalPref('floatingSidebar');
  const [budgetName] = useMetadataPref('budgetName');
  const dispatch = useDispatch();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState(
    location.hash === '#advanced' ? 'advanced' : 'general',
  );

  const onCloseBudget = () => {
    dispatch(closeBudget());
  };

  useEffect(() => {
    if (location.hash === '#advanced') {
      setActiveSection('advanced');
    }
  }, [location.hash]);

  useEffect(() => {
    const unlisten = listen('prefs-updated', () => {
      dispatch(loadPrefs());
    });

    dispatch(loadPrefs());
    return () => unlisten();
  }, [dispatch]);

  const { isNarrowWidth } = useResponsive();

  const sections = [
    { id: 'general', name: t('General'), icon: Info },
    { id: 'appearance', name: t('Appearance'), icon: Paintbrush },
    { id: 'formatting', name: t('Formatting'), icon: Type },
    { id: 'language', name: t('Language'), icon: Globe },
    { id: 'auth', name: t('Authentication'), icon: Shield },
    { id: 'encryption', name: t('Encryption'), icon: Lock },
    { id: 'budget-type', name: t('Budget Type'), icon: LayoutDashboard },
    ...(isElectron()
      ? [{ id: 'backups', name: t('Backups'), icon: HardDrive }]
      : []),
    { id: 'export', name: t('Export'), icon: Download },
    { id: 'advanced', name: t('Advanced'), icon: Settings2 },
  ];

  const active = sections.find(s => s.id === activeSection) ?? sections[0];

  function renderContent() {
    switch (activeSection) {
      case 'general':
        return <About />;
      case 'appearance':
        return <ThemeSettings />;
      case 'formatting':
        return <FormatSettings />;
      case 'language':
        return <LanguageSettings />;
      case 'auth':
        return <AuthSettings />;
      case 'encryption':
        return <EncryptionSettings />;
      case 'budget-type':
        return <BudgetTypeSettings />;
      case 'backups':
        return isElectron() ? <Backups /> : null;
      case 'export':
        return <ExportBudget />;
      case 'advanced':
        return (
          <>
            <AdvancedAbout />
            <ResetCache />
            <ResetSync />
            <RepairTransactions />
            <ExperimentalFeatures />
          </>
        );
      default:
        return null;
    }
  }

  if (isNarrowWidth) {
    return (
      <Page
        header={t('Settings')}
        style={{ paddingBottom: MOBILE_NAV_HEIGHT, backgroundColor: 'var(--background)' }}
      >
        <div
          data-testid="settings"
          className="mt-4 flex flex-col gap-4 w-full max-w-2xl"
        >
          <div className="flex flex-row items-end gap-3">
            <FormField>
              <FormLabel title={t('Budget name')} />
              <Input
                value={budgetName}
                disabled
                className="text-[var(--color-buttonNormalDisabledText)]"
              />
            </FormField>
            <Button onClick={onCloseBudget}>
              <Trans>Close budget</Trans>
            </Button>
          </div>
          <About />
          <ThemeSettings />
          <FormatSettings />
          <LanguageSettings />
          <AuthSettings />
          <EncryptionSettings />
          <BudgetTypeSettings />
          {isElectron() && <Backups />}
          <ExportBudget />
          <AdvancedAbout />
          <ResetCache />
          <ResetSync />
          <RepairTransactions />
          <ExperimentalFeatures />
        </div>
      </Page>
    );
  }

  return (
    <Page
      header={t('Settings')}
      style={{
        marginInline: floatingSidebar ? 'auto' : 0,
        backgroundColor: 'var(--background)',
      }}
    >
      <div
        data-testid="settings"
        className="flex flex-row gap-0 mt-2 pb-8"
      >
        {/* Left nav */}
        <nav className="w-48 shrink-0 flex flex-col gap-0.5 py-1 pr-3 border-r border-border">
          {sections.map(section => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={cn(
                'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm text-left w-full transition-colors',
                activeSection === section.id
                  ? 'bg-accent text-accent-foreground font-medium'
                  : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
              )}
            >
              <section.icon className="h-4 w-4 shrink-0" />
              {section.name}
            </button>
          ))}
        </nav>

        {/* Right content */}
        <div className="flex-1 flex flex-col min-w-0 pl-6 pb-8">
          {/* Breadcrumb */}
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-5 pt-1 shrink-0">
            <span>{t('Settings')}</span>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-foreground font-medium">{active.name}</span>
          </div>
          {/* Section content */}
          <div className="flex flex-col gap-4 max-w-2xl">{renderContent()}</div>
        </div>
      </div>
    </Page>
  );
}
